import { reactive } from 'vue';

// i18n
import { i18nManager } from '@martyrs/src/modules/core/views/classes/i18n.manager.js';
import locales from './locales/index.js';

// Store
import * as storeBlogposts from './store/blogposts.js';
import * as storeReactions from './store/reactions.js';

// Constructor (блоки контента) — через публичный интерфейс модуля
import { blockManager } from '@martyrs/src/modules/constructor/constructor.client.js';

// Router
import addRoutes from '@martyrs/src/modules/core/views/router/addRoutes.js';
import routerRoot from './router/community.router.js';
import routerUser from './router/community.user.router.js';
import routerOrganization from './router/community.organization.router.js';
import routerBackoffice from './router/community.backoffice.router.js';

// Views
// Layouts
import Community from './components/layouts/Community.vue';

// Pages
import Blog from './components/pages/Blog.vue';
import BlogPost from './components/pages/BlogPost.vue';
import CreateBlogPost from './components/pages/CreateBlogPost.vue';

// Blocks
import Activity from './components/blocks/Activity.vue';
import CardBlogpost from './components/blocks/CardBlogpost.vue';
import FooterBlogpost from './components/blocks/FooterBlogpost.vue';

// Sections
import Comments from './components/sections/Comments.vue';
import HotPosts from './components/sections/HotPosts.vue';

function initializeCommunity(app, store, router, options = {}) {
  // Регистрируем локализацию модуля (как у orders).
  i18nManager.register('community', locales);

  // Layout-компонент клиентского контекста community. По умолчанию — встроенный
  // 3-колоночный shell (Community.vue) со слотами #hero/#aside-left/#aside-right
  // и дефолтным наполнением. Проект ПЕРЕОПРЕДЕЛЯЕТ его через
  // global.config.modules.community.settings.layout: собственный компонент
  // оборачивает shell и кладёт в слоты свой контент (hero, навигацию по темам,
  // CTA и т.п.) — так наполнение колонок остаётся проектным, а геометрия общей.
  const communityLayout =
    options.settings?.layout ||
    (() => import(/* webpackChunkName: "community-layout" */ './components/layouts/Community.vue'));

  // Root: /community/*
  if (!options.contexts || options.contexts.includes('client')) addRoutes(router, {
    parentName: options.route || 'Home',
    basePath: 'community',
    component: communityLayout,
    routes: routerRoot,
    meta: {
      context: 'root',
      title: {
        en: 'Community',
        ru: 'Коммьюнити',
      },
      breadcrumbs: { hidden: true },
      title_hide: true,
    },
  });

  // User: /users/:_id/blogposts/*
  if (!options.contexts || options.contexts.includes('user')) {
    addRoutes(router, {
      parentName: options.routeUser || 'User Profile Root',
      basePath: 'blogposts',
      routes: routerUser,
      routeNamePrefix: 'User',
      meta: { context: 'user' },
    });
  }

  // Organization: /organizations/:_id/posts/*
  if (!options.contexts || options.contexts.includes('organization')) {
    addRoutes(router, {
      parentName: options.routeOrganizations || 'OrganizationRoot',
      basePath: 'posts',
      routes: routerOrganization,
      routeNamePrefix: 'Organization',
      meta: { context: 'organization' },
    });
  }

  // Backoffice: /backoffice/community/*
  if (!options.contexts || options.contexts.includes('backoffice')) {
    addRoutes(router, {
      parentName: options.routeBackoffice || 'Backoffice Root',
      basePath: 'community',
      routes: routerBackoffice,
      routeNamePrefix: 'Backoffice',
      meta: { context: 'backoffice' },
    });
  }

  store.addStore('blogposts', storeBlogposts);
  store.addStore('reactions', storeReactions);

  // Настройки модуля доступны через useStore().community.state — компоненты и
  // шаблоны читают `posting` отсюда. Значение приходит из global.config.js.
  // 'public' | 'auth' | 'admin'.
  const communityState = reactive({
    posting: options.posting || 'auth',
  });
  store.addStore('community', { state: communityState });

  // Кастомные блоки конструктора для статей блога. Community владеет блочным
  // контентом, поэтому регистрирует их в constructor при своей инициализации.
  // Проект РАСШИРЯЕТ набор через global.config.modules.community.settings.blocks
  // (компоненты живут в проекте). Значение — компонент или { viewer, editor, viewerProps }.
  const blocks = options.settings?.blocks;
  if (blocks) {
    for (const [type, def] of Object.entries(blocks)) {
      blockManager.register(type, def?.viewer ? def : { viewer: def });
    }
  }
}

const ModuleCommunity = {
  initialize: initializeCommunity,
  views: {
    store: {
      storeBlogposts,
      storeReactions,
    },
    router: {},
    components: {
      // Layouts
      Community,
      // Pages
      CreateBlogPost,
      BlogPost,
      Blog,
      // Blocks
      FooterBlogpost,
      Activity,
      CardBlogpost,
      // Sections
      Comments,
      HotPosts,
    },
  },
};

export { Activity, Blog, BlogPost, CardBlogpost, Comments, Community, CreateBlogPost, FooterBlogpost, HotPosts, storeBlogposts, storeReactions };

export default ModuleCommunity;
